"use client"

// Stripe Custom Checkout (Elements + Checkout Sessions API) — buyer-facing
// iframe, rendered INLINE in the cart/checkout rail (replaces the PayPal +
// wallet buttons in the same slot while open) instead of a modal overlay.
// Uses `CheckoutElementsProvider` + `<PaymentElement />` +
// `<ExpressCheckoutElement />` from `@stripe/react-stripe-js/checkout`,
// which — unlike the earlier `EmbeddedCheckoutProvider` — accepts an
// Elements `Appearance` (nested under `options.elementsOptions.appearance`),
// so the iframe interior follows the site's `next-themes` state.
//
// Session-mode history (see CHECKOUT_FORM_PROVIDER_MIGRATION.md):
//   - originally: `ui_mode: 'embedded_page'` + `EmbeddedCheckoutProvider`
//     (no appearance hook — iframe locked to Stripe defaults).
//   - short-lived: `ui_mode: 'form'` + `CheckoutFormProvider` — reverted
//     because `initCheckoutFormSdk` is a Stripe private-preview feature
//     and the API rejected our test account with
//     `Invalid ui_mode: must be one of hosted_page, embedded_page, or elements`.
//   - now: `ui_mode: 'elements'` + `CheckoutElementsProvider` — GA, full
//     Appearance API, wallets via a sibling `<ExpressCheckoutElement />`.
//
// UX pivot (2026-07-08): the Dialog overlay felt disconnected from the
// checkout page; the buyer wants the checkout to expand in place where the
// payment buttons live, not pop up over everything. Container is
// intentionally chromeless (no background/border/padding) — the iframe now
// carries the site theme, so no wrapping surface is needed.
//
// UX pivot #2 (2026-07-29): the "Pay with Stripe" CTA that used to gate this
// component is gone — `paypal-checkout.tsx` now mints the Session as soon as
// the checkout payload is complete and mounts this form directly, under a
// "Stripe methods" heading, alongside (not instead of) the PayPal rail. Two
// consequences here: (1) the Cancel affordance was removed — there is no CTA
// to fall back to, so "closing" the form would strand the buyer on that rail;
// (2) `open` is now driven by the parent's mint FSM reaching `ready`, not by
// a click, but its meaning is unchanged (no `open` → no Stripe.js CDN hit).
//
// @see .tondocs/stripe/CHECKOUT_FORM_PROVIDER_MIGRATION.md — full migration

import { useEffect, useMemo, useState } from "react"
import { loadStripe } from "@stripe/stripe-js"
import {
  CheckoutElementsProvider,
  ExpressCheckoutElement,
  PaymentElement,
  useCheckoutElements,
} from "@stripe/react-stripe-js/checkout"
import { useTheme } from "next-themes"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { buildCheckoutFormAppearance } from "@/lib/stripe/appearance"

export interface StripeEmbeddedCheckoutInlineProps {
  open: boolean
  clientSecret: string
  sellerStripeAccountId: string
  /**
   * Payment confirmed WITHOUT a Stripe-driven redirect (card without 3DS,
   * wallets). Parent must route the buyer to the return page via SPA
   * navigation (`router.push`), NOT a full page load — a full-page landing
   * remounts every root-layout provider and re-runs the parallel
   * refresh-token race that logs buyers out right after paying.
   * @see .tondocs/infra/AUTH_REFRESH_RACE.md
   */
  onSuccess: () => void
  /** Optional error surface — parent handles copy; we bubble load errors up. */
  onLoadError?: (err: Error) => void
}

export function StripeEmbeddedCheckoutInline({
  open,
  clientSecret,
  sellerStripeAccountId,
  onSuccess,
  onLoadError,
}: StripeEmbeddedCheckoutInlineProps) {
  const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY

  // Direct-charges scoping — the load-bearing gotcha. PaymentIntents behind
  // Connect direct charges live on the *seller's* connected account, not the
  // platform account. Without `stripeAccount` here, Stripe.js confirms
  // against the platform and the PaymentIntent 404s. Pinned by three
  // integration tests.
  //
  // Guarded on `open` — no `stripePromise` is minted (no Stripe.js CDN hit)
  // unless the parent has actually asked this component to render.
  //
  // NO `betas` array — Elements + Checkout Sessions API is GA. The previous
  // `custom_checkout_payment_form_1` flag was for the private-preview
  // CheckoutFormProvider path; dropping it here is deliberate.
  //
  // `developerTools.assistant.enabled: false` kills Stripe's test-mode
  // "Stripe Developers" widget — a `position: fixed`, `z-index: 99999`
  // 123×72 iframe pinned to the bottom-right of the VIEWPORT (not of our
  // container). It used to hover over empty space because the Stripe form was
  // alone in this slot; since both rails render together it lands on top of
  // the PayPal buttons. Because it's viewport-fixed, bottom padding would only
  // let the buyer scroll the button clear at the very end of the list — at any
  // other scroll offset whatever sits in that corner is still covered. Turning
  // the widget off removes the overlap at every offset. Test-mode only, so
  // this is a no-op in production; the cost is losing its test-card autofill
  // on staging (cards are in .tondocs/stripe/TESTING.md).
  const stripePromise = useMemo(() => {
    if (!open || !publishableKey) return null
    return loadStripe(publishableKey, {
      stripeAccount: sellerStripeAccountId,
      developerTools: { assistant: { enabled: false } },
    })
  }, [open, publishableKey, sellerStripeAccountId])

  // Effective theme drives the Elements `Appearance` (theme + variables) and
  // the remount `key` below. Stripe reads `appearance` on instance creation;
  // docs say it CAN be updated live, but a remount is simpler and matches
  // how `<StripeEmbeddedPage>` handles Connect theme changes.
  //
  // `resolvedTheme` is the source of truth since Phase 2 step 2f: the overlay
  // panel hosting this form follows the site theme, and the iframe — which
  // inherits no page CSS — has to match it via `Appearance`. (Until 2f a
  // `darkUi` prop pinned the iframe dark to match the forced-dark panels;
  // those no longer exist.)
  const { resolvedTheme } = useTheme()
  const themeMode: "dark" | "light" | undefined =
    resolvedTheme === "dark" || resolvedTheme === "light"
      ? resolvedTheme
      : undefined

  const appearance = useMemo(() => {
    if (!open) return undefined
    return buildCheckoutFormAppearance(themeMode)
  }, [open, themeMode])

  const [configError, setConfigError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setConfigError(
      publishableKey
        ? null
        : "Stripe is not configured (NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY missing).",
    )
  }, [open, publishableKey])

  // The pre-SDK failure mode we most care about — Stripe.js script blocked /
  // failed to download — surfaces as the `loadStripe` promise rejecting or
  // resolving to null. Bubble that up here; per-Element load errors can be
  // handled inside the provider tree in a follow-up if needed.
  useEffect(() => {
    if (!open || !stripePromise) return
    let cancelled = false
    stripePromise
      .then((stripe) => {
        if (cancelled) return
        if (!stripe) onLoadError?.(new Error("Stripe.js failed to initialize"))
      })
      .catch((err: unknown) => {
        if (cancelled) return
        onLoadError?.(err instanceof Error ? err : new Error("Failed to load Stripe.js"))
      })
    return () => {
      cancelled = true
    }
  }, [open, stripePromise, onLoadError])

  if (!open) return null

  const body = configError ? (
    <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
      {configError}
    </div>
  ) : !stripePromise ? (
    <div className="flex items-center justify-center py-10">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
    </div>
  ) : (
    // `key={themeMode}` remounts the whole SDK instance on a light↔dark
    // toggle so the new appearance takes effect without reasoning about
    // Stripe's live-update semantics. Same pattern as `<StripeEmbeddedPage>`
    // for Connect panels. `elementsOptions.appearance` is where the Elements
    // SDK expects the shape — NOT the top-level `options.appearance` used by
    // the CheckoutFormProvider variant.
    // `adaptivePricing.allowed` re-enables the behaviour we implicitly had
    // under `ui_mode: 'embedded_page'`: on a USD Session, EU buyers see
    // iDEAL / Bancontact / EPS / Wero (local PMs), and a Currency Selector
    // Element becomes mountable. Stripe converts at confirm time; the
    // Session's base currency stays USD, presented amount lands in
    // `checkout.session.completed → presentment_details.presentment_currency`.
    // Under `ui_mode: 'elements'` this is NOT default — it must be opted
    // into explicitly, otherwise PaymentElement filters strictly by
    // Session currency and hides local EU PMs.
    <CheckoutElementsProvider
      key={themeMode ?? "default"}
      stripe={stripePromise}
      options={{
        clientSecret,
        adaptivePricing: { allowed: true },
        elementsOptions: appearance ? { appearance } : undefined,
      }}
    >
      <EmbeddedCheckoutForm onSuccess={onSuccess} />
    </CheckoutElementsProvider>
  )

  // No theme scope of its own since Phase 2 step 2f: our DOM (the Pay button's
  // `bg-primary`, spinners, error banners) resolves theme tokens against
  // whatever scope the host panel provides, and the panel follows the site
  // theme. Only the iframe interior needs the explicit `Appearance` above —
  // it inherits no page CSS.
  return <div className="w-full">{body}</div>
}

// Inner composition — must live BELOW `<CheckoutElementsProvider>` because
// `useCheckoutElements()` throws if called outside the provider tree.
// Renders the wallet row (`<ExpressCheckoutElement />` for Apple/Google Pay
// — HARD BLOCKER per operator decision) above the card form
// (`<PaymentElement />`), plus a Pay button wired to `checkout.confirm()`.
// On confirm we ask Stripe to `redirect: 'if_required'` (the SDK default,
// but pinned explicitly): only PMs that structurally demand a top-level
// navigation (3DS challenge, iDEAL/Bancontact bank pages) redirect; plain
// cards and wallets resolve in place with `{ type: 'success' }` and we hand
// control to the parent via `onSuccess` for an SPA `router.push` to the
// return page. History: this used to be `redirect: 'always'` (scar
// 2026-07-08 — with nobody handling the success branch, non-3DS cards
// resolved silently and the buyer saw no confirmation). Forcing 'always'
// fixed the silence but reintroduced a full-page landing on
// `/checkout/stripe-return`, which remounts all root-layout providers and
// fires the parallel refresh-token race — buyers got logged out right
// after paying. Handling the success branch explicitly gives us the
// confirmation WITHOUT the full-page load.
// @see .tondocs/infra/AUTH_REFRESH_RACE.md
//
// NOTE: we intentionally do NOT render a `<form>` here. `<StripeEmbeddedCheckoutInline>`
// is mounted inside `checkout-form.tsx`'s `<form onSubmit=...>`, and HTML
// forbids nested forms — React emits a "form cannot contain a nested form"
// warning and Chromium drops the inner form's onSubmit handler entirely.
// The Pay button's click then bubbles to the outer form, which default-
// submits (method=GET) to the current URL, so the sheet reloads to `/feed?`
// and `runConfirm()` never fires. Wiring Pay as a plain `type="button"`
// with an onClick keeps the click self-contained.
function EmbeddedCheckoutForm({ onSuccess }: { onSuccess: () => void }) {
  const result = useCheckoutElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (result.type === "loading") {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    )
  }

  if (result.type === "error") {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {result.error.message}
      </div>
    )
  }

  const { checkout } = result

  const runConfirm = async (
    args?: Parameters<typeof checkout.confirm>[0],
  ) => {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      // `redirect: 'if_required'` — PMs that need a top-level navigation
      // (3DS challenge, iDEAL/Bancontact) still redirect and this promise
      // never resolves with success (the browser has left the page). PMs
      // that don't (plain cards, wallets) resolve `{ type: 'success' }`
      // here, and the parent SPA-navigates to the return page — see the
      // header comment above for why a Stripe-forced full-page redirect
      // logged buyers out (parallel refresh-token race on remount).
      const confirmed = await checkout.confirm({
        ...args,
        redirect: "if_required",
      })
      if (confirmed.type === "error") {
        setError(confirmed.error.message)
        setSubmitting(false)
        return
      }
      // Success without redirect. `submitting` stays true on purpose: the
      // parent's `onSuccess` is contractually responsible for tearing this
      // form down before / around its SPA-nav — either by unmounting the
      // wrapping `open` container OR by calling `closeUtility()` (overlay
      // context, spec §E step 1.3) when the cart is what hosts us. Soft-nav
      // within the `(site)` route group does NOT unmount `GlobalCartServer`,
      // so the previous "router.push will unmount us" assumption was wrong
      // and the button spun forever.
      onSuccess()
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Payment confirmation failed.",
      )
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-4">
      <ExpressCheckoutElement
        // Force Apple Pay + Google Pay buttons on. Stripe.js default is
        // `'auto'`, which hides Apple Pay in browsers without native support
        // (i.e. everything except Safari) — so Chrome desktop shows no Apple
        // Pay button at all. `'always'` matches the old `embedded_page`
        // behaviour: the button renders unconditionally and clicking it in a
        // non-Apple browser opens the cross-device QR flow (buyer scans →
        // completes on iPhone). Same for Google Pay: `'always'` guarantees
        // the button under the domain-registered PMD.
        //
        // The other five fields are explicit `undefined` to satisfy Stripe's
        // `StripeCheckoutExpressCheckoutElementOptions` type — the SDK
        // declares it without a `Partial<>` wrapper (all six fields required)
        // even though every field is semantically optional and accepts
        // `undefined` at runtime. Passing `undefined` restores the default
        // behaviour for each.
        options={{
          paymentMethods: { applePay: "always", googlePay: "always" },
          // `overflow: 'never'` disables the "one visible + chevron reveals
          // the rest" collapse and renders every wallet button inline. Default
          // is `'auto'`, which shows one button and hides the rest behind a
          // down-caret — hurts discoverability when we've deliberately turned
          // every wallet on. `maxColumns: 1` keeps them stacked full-width in
          // the rail (matches the sibling PayPal buttons below).
          layout: { maxColumns: 1, maxRows: undefined, overflow: "never" },
          buttonHeight: undefined,
          buttonTheme: undefined,
          buttonType: undefined,
          paymentMethodOrder: undefined,
        }}
        onConfirm={(event) => {
          // Wallets: user just authorised in the native sheet (Apple/Google
          // Pay). Hand the event back to `checkout.confirm` so the SDK
          // wires the payment method up to our Session before submission.
          void runConfirm({ expressCheckoutConfirmEvent: event })
        }}
      />
      {/* `layout: accordion` reveals every PM the Session returned in one
          column — matches how the old `embedded_page` variant showed card +
          iDEAL + Bancontact + EPS + Wero side by side. The SDK default is
          `tabs`, which hides everything past the first 2-3 into a "more"
          overflow. `defaultCollapsed` is intentionally omitted — Stripe.js
          logs it as an unrecognized parameter under `paymentElement.update()`
          and the accordion is expanded-by-first-item by default anyway. */}
      <PaymentElement options={{ layout: "accordion" }} />
      {error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}
      <Button
        type="button"
        disabled={submitting}
        onClick={() => void runConfirm()}
        className="w-full"
      >
        {submitting ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : (
          "Pay"
        )}
      </Button>
    </div>
  )
}
